/**
 * PersonalRemindersSuggestionsController — bloc dashboard payant §15.B
 * (EPIC-054 / 54.5).
 *
 * Affiche les rappels personnels en `status=suggested` pour l'organization
 * connectée, avec actions inline « Activer » / « Ignorer ». Visible
 * uniquement si ≥ 1 suggestion (sinon `hidden` posé au `connect()`).
 *
 * Source de vérité : `GET /me/personal-reminders?status=suggested`.
 * Mutations : `POST /me/personal-reminders/{id}/{activate|dismiss}`.
 *
 * Convention `<template>` + `data-slot` + `cloneTemplate()`/`fillSlots()`
 * — jamais de HTML inline dans le JS (CLAUDE.md règle 8).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const MAX_VISIBLE = 5;

export default class PersonalRemindersSuggestionsController extends BaseController {
    static targets = ["root", "list", "title", "loading", "rowTemplate"];

    connect() {
        this.rootTarget.hidden = true;
        this.loadSuggestions();
    }

    async loadSuggestions() {
        if (this.hasLoadingTarget) {
            this.loadingTarget.hidden = false;
        }
        try {
            const response = await api.get("/me/personal-reminders?status=suggested");
            const items = Array.isArray(response?.data) ? response.data : [];
            this.renderItems(items);
        } catch {
            // 401 redirigé globalement par api.js. Toute autre erreur →
            // bloc reste masqué (échec silencieux côté UI utilisateur,
            // alignement convention `feedback_silent_ui_errors`).
            this.rootTarget.hidden = true;
        } finally {
            if (this.hasLoadingTarget) {
                this.loadingTarget.hidden = true;
            }
        }
    }

    renderItems(items) {
        if (!Array.isArray(items) || items.length === 0) {
            this.rootTarget.hidden = true;
            return;
        }

        const visible = items.slice(0, MAX_VISIBLE);

        if (this.hasTitleTarget) {
            const count = items.length;
            this.titleTarget.textContent = count === 1
                ? "Notamicro vous suggère 1 rappel"
                : `Notamicro vous suggère ${count} rappels`;
        }

        this.listTarget.replaceChildren();
        for (const item of visible) {
            const fragment = this.rowTemplateTarget.content.cloneNode(true);
            this.fillRow(fragment, item);
            this.listTarget.appendChild(fragment);
        }

        this.rootTarget.hidden = false;
    }

    fillRow(fragment, item) {
        const root = fragment.querySelector('[data-slot="root"]');
        const titleEl = fragment.querySelector('[data-slot="title"]');
        const activateBtn = fragment.querySelector('[data-slot="activate-btn"]');
        const dismissBtn = fragment.querySelector('[data-slot="dismiss-btn"]');

        if (root && item?.id) {
            root.dataset.reminderId = String(item.id);
        }
        if (titleEl) {
            titleEl.textContent = item?.title ?? "";
        }
        if (activateBtn) {
            activateBtn.addEventListener("click", () => this.activate(item.id, root));
        }
        if (dismissBtn) {
            dismissBtn.addEventListener("click", () => this.dismiss(item.id, root));
        }
    }

    async activate(id, rowEl) {
        await this.mutate(id, "activate", rowEl);
    }

    async dismiss(id, rowEl) {
        await this.mutate(id, "dismiss", rowEl);
    }

    async mutate(id, action, rowEl) {
        if (!id || !rowEl) return;
        const buttons = rowEl.querySelectorAll("button");
        for (const btn of buttons) btn.disabled = true;
        try {
            await api.post(`/me/personal-reminders/${id}/${action}`, {});
            rowEl.remove();
            if (this.listTarget.children.length === 0) {
                this.rootTarget.hidden = true;
            } else if (this.hasTitleTarget) {
                const remaining = this.listTarget.children.length;
                this.titleTarget.textContent = remaining === 1
                    ? "Notamicro vous suggère 1 rappel"
                    : `Notamicro vous suggère ${remaining} rappels`;
            }
        } catch {
            for (const btn of buttons) btn.disabled = false;
        }
    }
}
